import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { toast } from "sonner";
import {
  ClockCounterClockwise, FunnelSimple, CaretLeft, CaretRight,
  User, Ticket, Briefcase, Car, Gear, SignIn, Trash, PencilSimple, Plus,
} from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";

const ACTION_ICONS = {
  crear: <Plus size={14} className="text-emerald-500" />,
  login: <SignIn size={14} className="text-blue-500" />,
  actualizar: <PencilSimple size={14} className="text-amber-500" />,
  eliminar: <Trash size={14} className="text-red-500" />,
};

const MODULE_ICONS = {
  Tickets: <Ticket size={14} className="text-blue-600" />,
  Comercial: <Briefcase size={14} className="text-purple-600" />,
  Viaticos: <Car size={14} className="text-amber-600" />,
  Usuarios: <User size={14} className="text-emerald-600" />,
  Sistema: <Gear size={14} className="text-slate-500" />,
};

const MODULE_COLORS = {
  Tickets: "bg-blue-50 text-blue-700 border-blue-200",
  Comercial: "bg-purple-50 text-purple-700 border-purple-200",
  Viaticos: "bg-amber-50 text-amber-700 border-amber-200",
  Usuarios: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Sistema: "bg-slate-50 text-slate-600 border-slate-200",
};

const ACTION_COLORS = {
  crear: "bg-emerald-50 text-emerald-700",
  login: "bg-blue-50 text-blue-700",
  actualizar: "bg-amber-50 text-amber-700",
  eliminar: "bg-red-50 text-red-700",
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function AuditLogModule() {
  const { token } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filterModule, setFilterModule] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [searchDate, setSearchDate] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/audit-log?page=${page}&limit=50`;
      if (filterModule !== "all") url += `&module=${filterModule}`;
      if (filterAction !== "all") url += `&action=${filterAction}`;
      if (searchDate) url += `&start_date=${searchDate}&end_date=${searchDate}`;
      const { data: d } = await axios.get(url, { headers });
      setData(d);
    } catch { toast.error("Error cargando bitacora"); }
    setLoading(false);
  }, [token, page, filterModule, filterAction, searchDate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/audit-log/modules`, { headers }).then(r => {
      setModules(r.data.modules);
      setActions(r.data.actions);
    }).catch(() => {});
  }, [token]);

  const resetFilters = () => { setFilterModule("all"); setFilterAction("all"); setSearchDate(""); setPage(1); };

  return (
    <div className="space-y-4" data-testid="audit-log-module">
      <div className="flex items-center gap-3 mb-2">
        <ClockCounterClockwise size={24} weight="duotone" className="text-slate-700" />
        <div>
          <h2 className="text-xl font-bold text-slate-900">Bitacora del Sistema</h2>
          <p className="text-sm text-slate-500">Historial de todas las acciones realizadas en el software</p>
        </div>
        <span className="ml-auto text-xs bg-slate-100 text-slate-600 px-3 py-1.5 rounded-full font-medium" data-testid="audit-log-total">
          {data.total} registros
        </span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border p-3 shadow-sm" data-testid="audit-log-filters">
        <FunnelSimple size={16} className="text-slate-400" />
        <Select value={filterModule} onValueChange={(v) => { setFilterModule(v); setPage(1); }}>
          <SelectTrigger className="w-[160px] text-xs h-8" data-testid="audit-filter-module">
            <SelectValue placeholder="Modulo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los modulos</SelectItem>
            {modules.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterAction} onValueChange={(v) => { setFilterAction(v); setPage(1); }}>
          <SelectTrigger className="w-[160px] text-xs h-8" data-testid="audit-filter-action">
            <SelectValue placeholder="Accion" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las acciones</SelectItem>
            {actions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={searchDate} onChange={(e) => { setSearchDate(e.target.value); setPage(1); }}
          className="w-[160px] text-xs h-8" data-testid="audit-filter-date" />
        {(filterModule !== "all" || filterAction !== "all" || searchDate) && (
          <button onClick={resetFilters} className="text-xs text-blue-600 hover:underline" data-testid="audit-clear-filters">Limpiar filtros</button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border">
          <ClockCounterClockwise size={40} className="mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 font-medium">Sin registros en la bitacora</p>
          <p className="text-xs text-slate-400 mt-1">Las acciones del sistema se registraran aqui automaticamente</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="overflow-auto">
            <table className="w-full text-xs" data-testid="audit-log-table">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="text-left py-2.5 px-3 font-semibold w-[170px]">Fecha y Hora</th>
                  <th className="text-left py-2.5 px-3 font-semibold w-[130px]">Usuario</th>
                  <th className="text-left py-2.5 px-3 font-semibold w-[110px]">Modulo</th>
                  <th className="text-left py-2.5 px-3 font-semibold w-[100px]">Accion</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item, i) => (
                  <tr key={item.id || i} className="border-t hover:bg-slate-50/70 transition-colors" data-testid={`audit-row-${i}`}>
                    <td className="py-2.5 px-3 text-slate-500 whitespace-nowrap font-mono">{fmtDate(item.created_at)}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-1.5">
                        <User size={13} className="text-slate-400 shrink-0" />
                        <span className="text-slate-700 font-medium truncate max-w-[110px]">{item.user_name}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${MODULE_COLORS[item.module] || MODULE_COLORS.Sistema}`}>
                        {MODULE_ICONS[item.module] || MODULE_ICONS.Sistema}
                        {item.module}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${ACTION_COLORS[item.action] || "bg-slate-50 text-slate-600"}`}>
                        {ACTION_ICONS[item.action] || <Gear size={14} />}
                        {item.action}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-slate-600 max-w-[400px] truncate">{item.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
              <span className="text-xs text-slate-500">
                Pagina {data.page} de {data.pages} ({data.total} registros)
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="p-1.5 rounded-lg hover:bg-white disabled:opacity-30" data-testid="audit-prev-page">
                  <CaretLeft size={16} />
                </button>
                <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page >= data.pages}
                  className="p-1.5 rounded-lg hover:bg-white disabled:opacity-30" data-testid="audit-next-page">
                  <CaretRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* How it works */}
      <div className="bg-slate-50 border rounded-xl p-4 text-xs text-slate-500">
        <p className="font-semibold text-slate-600 mb-1">Como funciona la Bitacora</p>
        <ul className="space-y-0.5 list-disc list-inside">
          <li>Se registra automaticamente cada inicio de sesion, creacion, edicion y eliminacion</li>
          <li>Cubre los modulos: Tickets, Comercial, Viaticos, Usuarios y Sistema</li>
          <li>Usa los filtros para buscar por modulo, accion o fecha especifica</li>
          <li>Los registros se conservan indefinidamente para auditoria</li>
        </ul>
      </div>
    </div>
  );
}
